import { Dispatch } from "redux";
import { OpenAlert } from "../../../redux/alert/action";
import { AppDispatch } from "../../../redux/store";
import { LIMIT_SUGGESTION } from "../constants";
import { generateChatId, getInfoByType } from "../functions";
import {
  ChangeRolePlayerForm,
  ChatListType,
  ChatUserMetaData,
  ConversationsType,
  CreateUpdateGroupChannelType,
  MemberShipType,
  ParamsMembersAction,
  ParamsSuggestions,
} from "../types";
import { ConversationTypeEnum } from "../types/enums";
import * as requestToServer from "./chatCruds";
import { chatsSlice } from "./chatSlice";
import i18n from "../../../i18n";

const { actions } = chatsSlice;

const getAllChats =
  (conversationsLength?: number, limit = 10, page = 0) =>
  (dispatch: Dispatch) => {
    if (!conversationsLength) {
      dispatch(actions.loadingConversationList(true));
    }
    return requestToServer
      .fetchAllChats(limit, page)
      .then((response: any) => {
        if (response?.ok) {
          const conversationList = response?.data?.results as ChatListType[];
          dispatch(
            actions.conversationsFetched({
              conversationList: conversationList,
              pagination: response?.data?.pagination ?? {},
              page,
            })
          );
        } else {
          dispatch(
            OpenAlert({ msg: response?.message, open: true, variant: "error" })
          );
        }
        return response;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loadingConversationList(false));
      });
  };

const getProfilePlayerById = (id: string) => (dispatch: Dispatch) => {
  dispatch(actions.loading(true));

  return requestToServer
    .fetchProfilePlayerById(id)
    .then((response: any) => {
      if (response?.ok) {
        dispatch(actions.setPlayerById(response?.data));
      } else {
        response?.message &&
          dispatch(
            OpenAlert({ msg: response?.message, open: true, variant: "error" })
          );
      }
      return response;
    })
    .catch((error) => {
      error?.message &&
        dispatch(
          OpenAlert({ msg: error?.message ?? "", open: true, variant: "error" })
        );
    })
    .finally(() => {
      dispatch(actions.loading(false));
    });
};

const setActivePersonalChat =
  (senderId: string, reciverId: string, id?: string) =>
  (dispatch: AppDispatch) => {
    return dispatch(getUserMetaData(Number(reciverId))).then(
      (response: any) => {
        if (response?.ok && Object.keys(response?.data)?.length > 0) {
          const chatId = generateChatId(Number(senderId), Number(reciverId));
          dispatch(actions.setEmptyAdjustedMessages());
          dispatch(
            actions.setSelectedChat({
              _id: id ?? "",
              chatId: chatId,
              type: ConversationTypeEnum.PERSONAL,
              unread: 0,
              user: response?.data,
            })
          );
          dispatch(actions.setChatState(true));
          return response?.data;
        }
      }
    );
  };

const getUserMetaData = (id: number) => (dispatch: AppDispatch) => {
  return requestToServer
    .fetchUserMetaData(id)
    .then((response: any) => {
      if (response?.ok) {
        dispatch(actions.setMetaData(response?.data as ChatUserMetaData));
      } else {
        response?.message &&
          dispatch(
            OpenAlert({ msg: response?.message, open: true, variant: "error" })
          );
      }
      return response;
    })
    .catch((error) => {
      error?.message &&
        dispatch(
          OpenAlert({ msg: error?.message ?? "", open: true, variant: "error" })
        );
    })
    .finally(() => {
      dispatch(actions.loading(false));
    });
};

const getPersonalMessages =
  (userId: number, chatId: string, page: number) => (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    dispatch(actions.loadingMessages(true));
    return requestToServer
      .fetchPersonalMessages(userId, chatId, page)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.setMessages({
              page: page,
              total: response.data?.total,
              messages: response.data?.data,
              chatId: chatId,
            })
          );
          dispatch(actions.setAdjustedMessages());
        } else {
          dispatch(
            OpenAlert({ msg: response?.message, open: true, variant: "error" })
          );
        }
        return response;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
        dispatch(actions.loadingMessages(false));
      });
  };

const getPlayers =
  ({ filter = "", count = 10, page = 1 }: ParamsSuggestions) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .fetchPlayers(filter, count, page)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.setPlayers({
              players: response?.data?.data,
              page,
            })
          );
        } else {
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "error",
              })
            );
        }
        return response;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const getMembers =
  ({ conversationId, count = 50, page = 0 }: ParamsMembersAction) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .fetchMembers(conversationId, count, page)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.setMembers({
              members: response?.data?.results,
              page,
            })
          );
        } else {
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "error",
              })
            );
        }
        return response;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const getGroupChannelMessages =
  (conversationId: string, page: number) => (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    dispatch(actions.loadingMessages(true));
    return requestToServer
      .fetchGroupChannelMessages(conversationId, page)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.setMessages({
              page: page,
              total: response.data?.total,
              messages: response.data?.data,
              chatId: conversationId,
            })
          );
          dispatch(actions.setAdjustedMessages());
        } else {
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "error",
              })
            );
        }
        return response;
      })
      .catch((error) => {
        dispatch(
          OpenAlert({ msg: error?.message ?? "", open: true, variant: "error" })
        );
      })
      .finally(() => {
        dispatch(actions.loading(false));
        dispatch(actions.loadingMessages(false));
      });
  };

const getGroupChannel =
  ({ name = "", count = 10, page = 0, type }: ParamsSuggestions) =>
  async (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .fetchGroupChannel(name, count, page, type)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.setChatListByType({
              data: response?.data?.results,
              page,
              type,
            })
          );
        } else {
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "error",
              })
            );
        }
        return response;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const fetchMainExplore = () => async (dispatch: AppDispatch) => {
  dispatch(actions.loading(true));
  const fetchPlayersPromise = dispatch(getPlayers({ count: LIMIT_SUGGESTION }));
  // const fetchChannelsPromise = dispatch(
  //   getGroupChannel({
  //     count: LIMIT_SUGGESTION * 2,
  //     type: ConversationTypeEnum.CHANNEL,
  //   })
  // );
  const fetchGroupsPromise = dispatch(
    getGroupChannel({
      count: LIMIT_SUGGESTION * 2,
      type: ConversationTypeEnum.GROUP,
    })
  );

  await Promise.all([fetchPlayersPromise, fetchGroupsPromise]).finally(() => {
    dispatch(actions.loading(false));
  });

  return Promise.resolve(true);
};

const deleteConversation =
  (conversationId: string, conversationType: ConversationsType) =>
  (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .removeConversation(conversationId)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.deletedConversation({
              _id: conversationId,
              type: conversationType,
            })
          );
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "success",
              })
            );
        }
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const leaveConversation =
  (conversationId: string, conversationType: ConversationsType) =>
  (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .leftConversation(conversationId)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.deletedConversation({
              _id: conversationId,
              type: conversationType,
            })
          );
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "success",
              })
            );
        }
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const kickMemberFromConversation =
  (conversationId: string, memberId: number) => (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .ejectMember(conversationId, memberId)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.kickedConversation({
              conversationId,
              memberId,
            })
          );
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "success",
              })
            );
        }
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const joinToConversation = (chat: ChatListType) => (dispatch: AppDispatch) => {
  dispatch(actions.loading(true));
  return requestToServer
    .subscribeInConversation(chat?._id)
    .then((response: any) => {
      if (response?.ok) {
        dispatch(
          actions.subscribedInConversation({
            chat,
          })
        );
        dispatch(
          OpenAlert({
            msg:
              response?.message ??
              (chat?.type === ConversationTypeEnum.GROUP
                ? i18n.t("common:chat.toasts.joinGroupSuccess")
                : i18n.t("common:chat.toasts.joinChannelSuccess")),
            open: true,
            variant: "success",
          })
        );
      }
    })
    .catch((error) => {
      error?.message &&
        dispatch(
          OpenAlert({
            msg: error?.message ?? "",
            open: true,
            variant: "error",
          })
        );
    })
    .finally(() => {
      dispatch(actions.loading(false));
    });
};

const createGrpChnl =
  (formValues: CreateUpdateGroupChannelType) => (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .requestCreateGrpChnl(formValues)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            OpenAlert({
              msg:
                response?.message ??
                `${formValues?.type} created successfully.`,
              open: true,
              variant: "success",
            })
          );
        }
        return response?.data;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const updateGrpChnl =
  (
    selectedChat: ChatListType,
    conversationId: string,
    formValues: CreateUpdateGroupChannelType
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .requestUpdateGrpChnl(conversationId, formValues)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.setSelectedChat({
              ...selectedChat,
              ...response?.data,
              chatId: selectedChat?.chatId,
            })
          );
          dispatch(
            OpenAlert({
              msg: response?.message ?? response?.data?.message ?? "",
              open: true,
              variant: "success",
            })
          );
        }
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const changeRolePlayer =
  (conversationId: string, formValues: ChangeRolePlayerForm) =>
  (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .requestChangeRolePlayer(conversationId, formValues)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            OpenAlert({
              msg: response?.message ?? response?.data?.message ?? "",
              open: true,
              variant: "success",
            })
          );
        }
        return response?.data;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const deleteMessage =
  (conversationId: string, messageId: string) => (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .removeMessage(conversationId, messageId)
      .then((response: any) => {
        if (response?.ok) {
          response?.data?.message &&
            dispatch(
              OpenAlert({
                msg: response?.data?.message,
                open: true,
                variant: "success",
              })
            );
        }
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

const getMyMembershipForConversation =
  (conversationId: string, userId: string) => (dispatch: Dispatch) => {
    dispatch(actions.loadingMessages(true));
    return requestToServer
      .fetchMembershipUserIdInConversation(conversationId, userId)
      .then((response: any) => {
        if (response?.ok) {
          dispatch(
            actions.updateMembershipSeletcedChat(
              response?.data?.membership as MemberShipType
            )
          );
          return response?.data?.membership;
        } else {
          dispatch(
            OpenAlert({ msg: response?.message, open: true, variant: "error" })
          );
        }
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loadingMessages(false));
      });
  };

const getGroupByExternalId =
  (externalId: number, userInfoId: number) => async (dispatch: AppDispatch) => {
    dispatch(actions.loading(true));
    return requestToServer
      .fetchGroupByExternalId(externalId)
      .then((response: any) => {
        let conversation = {} as ChatListType;
        if (response?.ok) {
          conversation = response?.data?.conversation;
          const userRole = conversation?.members?.find((member) => {
            return member.id === userInfoId;
          });
          if (userRole) {
            conversation.membership = {
              role: userRole?.role || "member",
              readAt: "",
              joinedAt: userRole?.joinedAt ?? "",
              isMuted: userRole?.isMuted ?? false,
            };
          }
          conversation = getInfoByType(conversation);
        } else {
          response?.message &&
            dispatch(
              OpenAlert({
                msg: response?.message,
                open: true,
                variant: "error",
              })
            );
        }
        return conversation;
      })
      .catch((error) => {
        error?.message &&
          dispatch(
            OpenAlert({
              msg: error?.message ?? "",
              open: true,
              variant: "error",
            })
          );
      })
      .finally(() => {
        dispatch(actions.loading(false));
      });
  };

// Translates a message into `lang` (the player's UI language); resolves to whether it worked.
// The message shows its own loading state, so the chat-wide loading flag is left alone.
const getMessageTrasnlateById =
  (chatId: string, messageId: string, lang: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    const failed = (status?: number) => {
      dispatch(
        OpenAlert({
          msg: i18n.t(
            status === 429
              ? "common:chat.toasts.translateTooMany"
              : "common:chat.toasts.translateFailed"
          ),
          open: true,
          variant: "error",
        })
      );
      return false;
    };
    return requestToServer
      .fetchMessageTrasnlateById(messageId, lang)
      .then((response: any) => {
        if (response?.ok && typeof response?.data?.translatedText === "string") {
          dispatch(
            actions.setTranslatedMessage({
              responseTranslate: response?.data,
              chatId: chatId,
              messageId: messageId,
              lang,
            })
          );
          return true;
        }
        return failed(response?.status);
      })
      .catch((error) => failed(error?.status));
  };

export {
  changeRolePlayer,
  createGrpChnl,
  deleteConversation,
  deleteMessage,
  fetchMainExplore,
  getAllChats,
  getGroupChannel,
  getGroupChannelMessages,
  getMembers,
  getMyMembershipForConversation,
  getPersonalMessages,
  getPlayers,
  getProfilePlayerById,
  getUserMetaData,
  joinToConversation,
  kickMemberFromConversation,
  leaveConversation,
  setActivePersonalChat,
  updateGrpChnl,
  getGroupByExternalId,
  getMessageTrasnlateById,
};
